import { countConditions } from "@estelle/domain";
import type {
  DocAttachment,
  DocFolder,
  DocFolderWithAccess,
  DocGrant,
  DocPage,
  DocPageEvent,
  DocPageWithContent,
  DocPermissionLevel,
  DocRevision,
  DocsRepository,
  Id,
  ListQuery,
  Page,
  WriteResult,
} from "@estelle/domain";

import { API_BASE, apiFetch } from "@/adapters/api/base";

/**
 * Docs, over HTTP (M12-5, FR-DOC-1 … FR-DOC-11, ticket #381).
 *
 * The API returns the domain shapes as `apps/api/src/routes/docs.ts`
 * documents them; nothing is remapped here. Every read is already filtered
 * to what the caller can see (FR-DOC-9) — an unreadable folder is absent
 * from `listFolders`, an unreadable page's `getPage` and `listRevisions`
 * come back empty or null, never a thrown 403 — so this adapter has nothing
 * permission-shaped to add on top of what the server already decided.
 */

const BASE = "/api/docs";

function listParams(query: ListQuery | undefined): URLSearchParams {
  const params = new URLSearchParams();
  if (!query) return params;
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    params.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    params.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    params.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return params;
}

async function get<T>(path: string, params?: URLSearchParams): Promise<T> {
  const query = params?.toString() ?? "";
  const response = await apiFetch(`${BASE}${path}${query ? `?${query}` : ""}`);
  if (!response.ok) {
    throw new Error(
      `Docs request failed: ${String(response.status)} on ${path || "/"}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. Mirrors
 * `adapters/api/savedViews.ts`'s `write`.
 */
async function write<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Docs ${method} failed: ${String(response.status)} on ${path || "/"}`,
  );
}

export function createApiDocs(): DocsRepository {
  return {
    listFolders: () => get<DocFolderWithAccess[]>("/folders"),

    listPages: (folderId, query) =>
      get<Page<DocPage>>(
        `/folders/${encodeURIComponent(folderId)}/pages`,
        listParams(query),
      ),

    getPage: async (id, revisionId) => {
      const params = revisionId
        ? new URLSearchParams({ revisionId })
        : undefined;
      const query = params?.toString() ?? "";
      const response = await apiFetch(
        `${BASE}/pages/${encodeURIComponent(id)}${query ? `?${query}` : ""}`,
      );
      if (!response.ok) {
        throw new Error(`Docs request failed: ${String(response.status)}`);
      }
      return (await response.json()) as DocPageWithContent | null;
    },

    listRevisions: (pageId, query) =>
      get<Page<DocRevision>>(
        `/pages/${encodeURIComponent(pageId)}/revisions`,
        listParams(query),
      ),

    listPageEvents: (pageId, query) =>
      get<Page<DocPageEvent>>(
        `/pages/${encodeURIComponent(pageId)}/events`,
        listParams(query),
      ),

    search: (term, query) => {
      const params = listParams(query);
      params.set("term", term);
      return get<Page<DocPage>>("/search", params);
    },

    savePage: (id, markdown, message) =>
      write<DocPageWithContent>(
        `/pages/${encodeURIComponent(id)}/save`,
        "POST",
        {
          markdown,
          message,
        },
      ),

    restoreRevision: (pageId, revisionId) =>
      write<DocPageWithContent>(
        `/pages/${encodeURIComponent(pageId)}/restore`,
        "POST",
        { revisionId },
      ),

    createPage: (folderId, title) =>
      write<DocPageWithContent>(
        `/folders/${encodeURIComponent(folderId)}/pages`,
        "POST",
        { title },
      ),

    deletePage: async (id) => {
      const result = await write<null>(
        `/pages/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    listDeletedPages: (folderId) =>
      get<DocPage[]>(`/folders/${encodeURIComponent(folderId)}/deleted-pages`),

    restorePage: (id) =>
      write<DocPage>(`/pages/${encodeURIComponent(id)}/undelete`, "POST"),

    movePage: (id, folderId) =>
      write<DocPage>(`/pages/${encodeURIComponent(id)}/move`, "POST", {
        folderId,
      }),

    renamePage: (id, title) =>
      write<DocPage>(`/pages/${encodeURIComponent(id)}`, "PATCH", { title }),

    createFolder: (parentId, name) =>
      write<DocFolderWithAccess>("/folders", "POST", { parentId, name }),

    renameFolder: (id, name) =>
      write<DocFolder>(`/folders/${encodeURIComponent(id)}`, "PATCH", { name }),

    moveFolder: (id, parentId) =>
      write<DocFolder>(`/folders/${encodeURIComponent(id)}/move`, "POST", {
        parentId,
      }),

    listGrants: (folderId) =>
      get<DocGrant[]>(`/folders/${encodeURIComponent(folderId)}/grants`),

    setGrant: (
      folderId: Id,
      principalType: "user" | "group",
      principalId: Id,
      level: DocPermissionLevel,
    ) =>
      write<DocGrant>(
        `/folders/${encodeURIComponent(folderId)}/grants`,
        "POST",
        {
          principalType,
          principalId,
          level,
        },
      ),

    removeGrant: async (id) => {
      const result = await write<null>(
        `/grants/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    listAttachments: (pageId) =>
      get<DocAttachment[]>(`/pages/${encodeURIComponent(pageId)}/attachments`),

    uploadAttachment: async (pageId, file) => {
      const body = new FormData();
      // `Uint8Array` first: `Blob`'s constructor accepts a typed array
      // directly, and this is the one place the raw bytes this interface
      // promises actually leave that shape, right before the browser's own
      // multipart encoder takes over.
      body.append(
        "file",
        new Blob([file.data.slice()], { type: file.contentType }),
        file.filename,
      );
      const response = await apiFetch(
        `${BASE}/pages/${encodeURIComponent(pageId)}/attachments`,
        { method: "POST", body },
      );
      const result = (await response
        .json()
        .catch(() => null)) as WriteResult<DocAttachment> | null;
      if (result !== null && typeof result === "object" && "ok" in result) {
        return result;
      }
      throw new Error(
        `Docs POST failed: ${String(response.status)} on /pages/${pageId}/attachments`,
      );
    },

    // The download route, never R2 directly (FR-DOC-12) — the bucket is
    // private, so every fetch of this URL is permission-checked on the way
    // through. Absolute, because the API is a different origin from the SPA
    // in every deployed environment (`base.ts`'s own comment).
    attachmentUrl: (id) =>
      `${API_BASE}${BASE}/attachments/${encodeURIComponent(id)}`,

    deleteAttachment: async (id) => {
      const result = await write<null>(
        `/attachments/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },
  };
}
